import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, FormControl, Spinner } from "react-bootstrap";
import axios from "axios";

// API: /api/Transactions/getWalletTransactionDetails
// Payload: { userid, fromDate, toDate }

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const formatINR = (v) => `₹ ${toNum(v).toFixed(2)}`;
const formatDateCompact = (v) => {
  if (!v) return "-";
  // API gives like "2/9/2026 3:12:42 PM" -> keep it neat
  return String(v).replace(/\s+/g, " ").trim();
};

const buildDeductionChips = (r) => {
  const chips = [];
  const pushChip = (label, amount) => {
    const n = toNum(amount);
    if (n > 0) chips.push({ label, amount: n });
  };

  pushChip("Commission", r?._commission);
  pushChip("Comm Tax", r?._commissionTax);
  pushChip("Delivery", r?._delivery);
  pushChip("Packaging", r?._packaging);

  return chips;
};


const getToday = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function WalletTransactionModal({
  show,
  onHide,
  userID,
  baseUrl,
  BASIC_AUTH,
}) {
  const [fromDate, setFromDate] = useState(getToday());
  const [toDate, setToDate] = useState(getToday());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const cfg = useMemo(
    () => ({
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${BASIC_AUTH}`,
      },
    }),
    [BASIC_AUTH]
  );

  const fetchTransactions = async () => {
    if (!userID || !fromDate || !toDate) return;

    if (new Date(toDate) < new Date(fromDate)) {
      setError("To Date cannot be before From Date");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const payload = { userid: userID, fromDate, toDate };

      const res = await axios.post(
        `${baseUrl}/api/Transactions/getWalletTransactionDetails`,
        payload,
        cfg
      );

      if (res?.data?.responseCode === "00" && Array.isArray(res.data.response)) {
        setRows(res.data.response);
      } else {
        setRows([]);
        setError(res?.data?.responseMessage || "No transactions found");
      }
    } catch (e) {
      setRows([]);
      setError("Failed to fetch wallet transactions");
    } finally {
      setLoading(false);
    }
  };

  // default current date + auto fetch on open
  useEffect(() => {
    if (show) {
      const today = getToday();
      setFromDate(today);
      setToDate(today);
      setTimeout(fetchTransactions, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Identify credited vs deducted (clear columns)
  const computedRows = useMemo(() => {
    return (rows || []).map((r) => {
      const credit = toNum(r?.settelment_value);

      const commission = toNum(r?.chaafoCommission);
      const commissionTax = toNum(r?.taxOnCommission);
      const delivery = toNum(r?.delivery_charges);
      const packaging = toNum(r?.packaging_charges);

      const debit = commission + commissionTax + delivery + packaging;
      const net = credit - debit;

      return {
        ...r,
        _credit: credit,
        _debit: debit,
        _net: net,
        _commission: commission,
        _commissionTax: commissionTax,
        _delivery: delivery,
        _packaging: packaging,
      };
    });
  }, [rows]);

  const totals = useMemo(() => {
    return computedRows.reduce(
      (acc, r) => {
        acc.credit += toNum(r._credit);
        acc.debit += toNum(r._debit);
        acc.net += toNum(r._net);
        return acc;
      },
      { credit: 0, debit: 0, net: 0 }
    );
  }, [computedRows]);

  return (
    <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Wallet Transaction Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
          <div>
            <div className="small text-muted">From Date</div>
            <FormControl
              type="date"
              value={fromDate}
              onChange={(e) => {
                const v = e.target.value;
                setFromDate(v);
                if (toDate && new Date(toDate) < new Date(v)) setToDate(v);
              }}
            />
          </div>

          <div>
            <div className="small text-muted">To Date</div>
            <FormControl
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <Button variant="dark" className="px-3" onClick={fetchTransactions} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" /> Loading
              </>
            ) : (
              "Fetch"
            )}
          </Button>

          <div className="ms-auto text-end">
            <div className="small text-muted">Totals</div>
            <div className="fw-semibold">
              <span className="me-3 text-success">Credit: {formatINR(totals.credit)}</span>
              <span className="me-3 text-danger">Debit: {formatINR(totals.debit)}</span>
              {/* <span className={totals.net >= 0 ? "text-success" : "text-danger"}>
                Net: {formatINR(totals.net)}
              </span> */}
            </div>
          </div>
        </div>

        {error ? <div className="alert alert-warning py-2">{error}</div> : null}

 <div className="table-responsive" style={{ maxHeight: "60vh" }}>
  <table className="table table-hover align-middle mb-0">
    <thead
      className="table-light"
      style={{ position: "sticky", top: 0, zIndex: 2 }}
    >
      <tr>
        <th>Date / Order</th>
        <th>Type</th>
        <th className="text-end">Amount</th>
        <th className="text-end">Credited</th>
        {/* <th className="text-end">Net</th> */}
      </tr>
    </thead>

    <tbody>
      {computedRows?.length ? (
        computedRows.map((r, idx) => {
          const deductions = [];

          if (r?._commission > 0)
            deductions.push(`Commission ${formatINR(r._commission)}`);
          if (r?._commissionTax > 0)
            deductions.push(`Tax ${formatINR(r._commissionTax)}`);
          if (r?._delivery > 0)
            deductions.push(`Delivery ${formatINR(r._delivery)}`);
          if (r?._packaging > 0)
            deductions.push(`Packaging ${formatINR(r._packaging)}`);

          return (
            <tr key={r?.id || idx}>
              {/* Date + Order */}
              <td>
                <div className="fw-semibold">{r?.in_date || "-"}</div>
                <div className="small text-muted">
                  Order #{r?.orderid || "-"}
                </div>
              </td>

              {/* Type */}
              <td className="text-capitalize">
                <span className="badge bg-light text-dark border">
                  {r?.order_type || "-"}
                </span>
              </td>

              {/* Amount */}
              <td className="text-end">
                <div className="fw-semibold">
                  {formatINR(r?.order_amount)}
                </div>

                {deductions.length > 0 && (
                  <div className="small text-muted mt-1">
                    − {deductions.join(" · ")}
                  </div>
                )}
              </td>

              {/* Credited */}
              <td className="text-end fw-semibold text-success">
                {formatINR(r?._credit)}
              </td>

              {/* Net */}
              {/* <td className="text-end">
                <span
                  className={`fw-bold ${
                    r?._net >= 0 ? "text-success" : "text-danger"
                  }`}
                >
                  {formatINR(r?._net)}
                </span>
              </td> */}
            </tr>
          );
        })
      ) : (
        <tr>
          <td colSpan={5} className="text-center py-4 text-muted">
            {loading ? "Loading..." : "No transactions"}
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>


      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
